import { Injectable } from '@angular/core';
import { Client, BatchRequestStep, BatchResponseContent, BatchRequestContent, GraphRequest } from '@microsoft/microsoft-graph-client';
import * as MicrosoftGraph from '@microsoft/microsoft-graph-types';
import jwtDecode from 'jwt-decode';
import { every, uniqBy } from 'lodash-es';
import { Observable, from, forkJoin, of } from 'rxjs';
import { map, take, switchMap, tap, skipWhile, catchError } from 'rxjs/operators';
import { HttpClient } from '@angular/common/http';
import { MicrosoftPermission } from '@nimbus-library/features/graph/enums/graph-permission.enum';
import { IApiToken, IAuthenticationService } from '@nimbus-library/features/authentication/services/i-authentication.service';
import { CacheService } from '@nimbus-library/features/cache';
import { FileService } from '@nimbus-library/features/shared/services/helper/file.service';
import { IGraphEntityRequest } from '../models/graph-entity-request';
import { GraphToken } from '../models/graph-token';

@Injectable({ providedIn: 'root' })
export class GraphApiClient {
  private readonly CACHE_IN_MONTHS = 6;
  private readonly client: Client;

  private readonly extendedSearchPermission = MicrosoftPermission.UserReadAll;
  private readonly presencePermission = MicrosoftPermission.PresenceReadAll;

  private readonly token$: Observable<IApiToken>;
  private token: IApiToken;
  public extendedSearchEnabled = false;
  public presenceEnabled = false;
  public initilized = false;

  public constructor(
    authService: IAuthenticationService,
    cacheService: CacheService,
    private readonly httpClient: HttpClient,
    private readonly fileService: FileService,
  ) {
    this.token$ = authService.getApiToken$(false, ['user.read']).pipe(skipWhile((token) => token === null));

    this.client = Client.init({
      authProvider: (done) => {
        this.token$.pipe(take(1)).subscribe({
          next: (token) => {
            this.setGraphToken(token, done);
          },
          error: (error) => done(error, null),
        });
      },
    });
  }

  public hasExtendedPermission(): Observable<boolean> {
    return this.hasPermissions([this.extendedSearchPermission]);
  }

  public getApiClientWithPermissions(): Observable<Client> {
    return this.initilized
      ? of(this.client)
      : this.token$.pipe(
          tap((token) => (this.extendedSearchEnabled = this.hasPermission(token.token, this.extendedSearchPermission))),
          map(() => this.client),
          tap(() => (this.initilized = true)),
        );
  }

  public getGraphEntityByIds<T>(entityRequests: IGraphEntityRequest<any, T>[]): Observable<Array<T>> {
    entityRequests = uniqBy(entityRequests, (x) => x.id?.toLowerCase());
    const requests: Array<BatchRequestStep> = entityRequests.map(
      (er) =>
        ({
          id: er.id,
          request: new Request(er.url),
          dependsOn: [],
        }) as BatchRequestStep,
    );

    return this.executeInBatch(requests).pipe(
      switchMap((batchResponse) => {
        const responseObjs: Array<Observable<T>> = [];
        let response: Response;
        for (const er of entityRequests) {
          response = batchResponse.getResponseById(er.id);
          if (response.status === 200) {
            const contentType = response.headers.get('Content-Type');
            if (contentType.includes('application/json')) {
              responseObjs.push(from(response.json()).pipe(map((json) => er.mapper(json))));
            } else {
              responseObjs.push(from(response.text()).pipe(map((buffer) => er.mapper({ id: er.id, body: buffer }))));
            }
          }
        }
        return responseObjs.length !== 0 ? forkJoin(responseObjs) : of([]);
      }),
    );
  }

  public executeInBatch(requests: Array<BatchRequestStep>): Observable<BatchResponseContent> {
    const batchRequestContent = new BatchRequestContent(requests);

    return from(batchRequestContent.getContent()).pipe(
      switchMap((content) => from(this.client.api('/$batch').post(content))),
      map((response) => new BatchResponseContent(response)),
    );
  }

  public getPhoto(userPrincipalName: string): Observable<string> {
    return this.httpClient
      .get(`https://graph.microsoft.com/v1.0/users/${encodeURI(userPrincipalName)}/photos/48x48/$value`, {
        responseType: 'blob',
        headers: {
          Authorization: `Bearer ${this.token.token}`,
        },
      })
      .pipe(
        catchError((err) => of(null)),
        switchMap((blob) => (blob ? this.fileService.convertBlobToBase64(blob) : of(null))),
      );
  }

  public getExchangePhoto(exchangeId: string): Observable<string> {
    return this.httpClient
      .get(`https://graph.microsoft.com/v1.0/me/contacts/${encodeURI(exchangeId)}/photo/$value`, {
        responseType: 'blob',
        headers: {
          Authorization: `Bearer ${this.token.token}`,
        },
      })
      .pipe(
        catchError((err) => of(null)),
        switchMap((blob) => (blob ? this.fileService.convertBlobToBase64(blob) : of(null))),
      );
  }

  public getPresences(userIds: string[]): Observable<{ value: { availability: string; activity: string; id: string }[] }> {
    const query = this.client.api('/communications/getPresencesByUserId');

    return from(query.post({ ids: userIds }));
  }

  public getManager(userId: string, params: string = ''): Observable<MicrosoftGraph.User> {
    const query = this.client.api(`/users/${userId}/manager?${params}`);
    return from(query.get());
  }

  public getDirectReports(userId: string, params: string = ''): Observable<MicrosoftGraph.User[]> {
    const query = this.client.api(`/users/${userId}/directReports?${params}`);
    return from(query.get()).pipe(map((x) => x?.value));
  }

  public request$<TModel>(uri: string, requestBuilder: (query: GraphRequest) => Promise<{ value: TModel }>): Observable<TModel> {
    const query = this.client.api(uri);
    return from(requestBuilder(query)).pipe(map((response) => response.value));
  }

  private hasPermission(token: string, permission: string): boolean {
    const decodedToken = this.decodeToken(token);

    return decodedToken.scp.includes(permission);
  }

  private decodeToken(token: string): GraphToken {
    return jwtDecode<GraphToken>(token);
  }

  private hasPermissions(permissions: string[]) {
    return this.token$.pipe(
      take(1),
      map((token) => (token ? every(permissions, (p) => this.hasPermission(token.token, p)) : false)),
    );
  }

  private setGraphToken(token: IApiToken, done: (error: any, accessToken: string) => void) {
    if (!token) {
      this.extendedSearchEnabled = false;
      this.presenceEnabled = false;
      this.token = null;
      done('Token permissions are missing', null);
    } else {
      this.extendedSearchEnabled = this.hasPermission(token.token, this.extendedSearchPermission);
      this.presenceEnabled = this.hasPermission(token.token, this.presencePermission);
      this.token = token;
      done(null, token.token);
    }
  }
}
